import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { format, parseISO } from "date-fns";
import { de } from "date-fns/locale";
import { apiGet } from "@/lib/api";
import type { EventDto } from "@/types";
import { RichText } from "@/components/content/RichText";
import { Breadcrumb } from "@/components/layout/Breadcrumb";
import { ResponsiveImage } from "@/components/content/ResponsiveImage";

export function EventPage() {
  const { slug = "" } = useParams();
  const locale = de;
  const { data: e, isLoading, error } = useQuery({
    queryKey: ["event", slug, "de"],
    queryFn: () => apiGet<EventDto>("/public/events/" + slug + "?locale=" + "de"),
    retry: false,
  });
  if (isLoading) return <div className="animate-pulse text-text-muted">...</div>;
  if (error || !e) return (
    <div>
      <h1 className="text-2xl font-bold">{"Diese Seite wurde leider nicht gefunden."}</h1>
      <Link to="/kalender" className="text-primary hover:underline">{"Kalender"}</Link>
    </div>
  );

  const ics = "/api/v1/public/events/" + e.slug + "/ics?locale=" + e.locale;

  return (
    <article>
      <Helmet>
        <title>{e.title} | Falkenrehde</title>
        {e.descriptionHtml && <meta name="description" content={stripHtml(e.descriptionHtml).slice(0, 160)} />}
      </Helmet>
      <Breadcrumb items={[{ label: "Kalender", to: "/kalender" }, { label: e.title }]} />

      <header className="mb-6">
        <h1 className="text-3xl lg:text-4xl font-bold">{e.title}</h1>
        <div className="text-text-muted mt-2 flex flex-wrap gap-x-3 gap-y-1">
          <time className="font-medium text-text">
            {format(parseISO(e.startAt), "EEEE, d. MMMM yyyy", { locale })}
          </time>
          {!e.allDay && (
            <span>
              {format(parseISO(e.startAt), "HH:mm", { locale })} Uhr
              {e.endAt && <> - {format(parseISO(e.endAt), "HH:mm", { locale })}</>}
            </span>
          )}
          {e.location && <span>- {e.location}</span>}
          {e.organiser && <span>- {e.organiser}</span>}
        </div>
      </header>

      {e.coverImage && (
        <figure className="mb-8">
          <ResponsiveImage media={e.coverImage} sizes="(min-width: 1024px) 720px, 100vw" className="w-full rounded-lg" loading="eager" />
        </figure>
      )}

      {e.descriptionHtml && <RichText html={e.descriptionHtml} />}

      <div className="mt-8 flex flex-wrap gap-3 items-center">
        <a href={ics} className="btn-primary" download>
          {"Zum Kalender hinzufügen"}
        </a>
        {e.locationMapUrl && (
          <a href={e.locationMapUrl} target="_blank" rel="noopener noreferrer" className="btn-ghost">
            Karte oeffnen
          </a>
        )}
      </div>
    </article>
  );
}

function stripHtml(html: string): string {
  return html.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
}
